/** Titel und urheberrechtlicher Hinweis eines Bodenrichtwerte-Dienstes, wie sie an jedem BRW angezeigt werden. */
export interface GroundValueService {
    title: string;
    license: string;
}

/**
 * Bodenrichtwerte-Dienste, nach dem Host ihres `weblink` (nicht der `url`, die auf den abgefragten
 * WFS/WMS zeigt und oft auf einem anderen Host liegt). Ohne Eintrag lehnt REVA die BRW ab.
 * Abgesichert durch `client/tests/object.groundvalue.services.test.ts`.
 */
export const GROUND_VALUE_SERVICES: ReadonlyMap<string, GroundValueService> = new Map([
    [
        "www.bodenrichtwerte-boris.de",
        {
            title: "Boris-D",
            license: "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) https://www.bodenrichtwerte-boris.de",
        },
    ],
    [
        "immobilienmarkt.niedersachsen.de",
        {
            title: "Bodenrichtwerte Immobilienmarkt.NI",
            license: "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) https://immobilienmarkt.niedersachsen.de",
        },
    ],
    [
        "www.boris.nrw.de",
        {title: "BORIS-NRW", license: "dl-de/zero-2-0 (www.govdata.de/dl-de/zero-2-0) https://www.boris.nrw.de"},
    ],
    [
        "www.gutachterausschuesse-bw.de",
        {
            title: "BORIS-BW",
            license: "© Landesamt für Geoinformation und Landentwicklung Baden-Württemberg, LGL, www.lgl-bw.de",
        },
    ],
    [
        "atlas.bayern.de",
        {
            title: "Bodenrichtwerte BayernAtlas",
            license: "cc-by-nd 4.0 (creativecommons.org/licenses/by-nd/4.0/) https://geoportal.bayern.de",
        },
    ],
    [
        "www.geoportal.hessen.de",
        {
            title: "BORIS Hessen",
            license: "dl-de/zero-2-0 (www.govdata.de/dl-de/zero-2-0) https://www.geoportal.hessen.de",
        },
    ],
    [
        "geoportal-hamburg.de",
        {
            title: "BORIS.HH",
            license:
                "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) Freie und Hansestadt Hamburg, Landesbetrieb Geoinformation und Vermessung",
        },
    ],
    [
        "ims.kiel.de",
        {
            title: "Bodenrichtwerte ims.kiel.de",
            license: "cc-by 4.0 (creativecommons.org/licenses/by/4.0) © GeoBasis-DE/LVermGeo SH/CC-BY 4.0",
        },
    ],
    [
        "geoportal.saarland.de",
        {
            title: "BORIS.SL",
            license:
                "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) © Landesamt für Vermessung, Geoinformation und Landentwicklung (LVGL)",
        },
    ],
    [
        "www.lvermgeo.sachsen-anhalt.de",
        {title: "BORIS.ST", license: "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) © GeoBasis-DE / LVermGeo ST"},
    ],
    [
        "www.geodatenportal.sachsen-anhalt.de",
        {title: "BORIS.ST", license: "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) © GeoBasis-DE / LVermGeo ST"},
    ],
    [
        "geoviewer.sachsen.de",
        {
            title: "BORIS Sachsen",
            license: "© Landesamt für Geobasisinformation Sachsen (GeoSN), www.boris.sachsen.de",
        },
    ],
    [
        "danord.gdi-sh.de",
        {
            title: "Bodenrichtwerte SH",
            license: "cc-by 4.0 (creativecommons.org/licenses/by/4.0) © GeoBasis-DE/LVermGeo SH/CC-BY 4.0",
        },
    ],
    [
        "maps.rlp.de",
        {
            title: "VBoris RLP",
            license: "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) © GeoBasis-DE / LVermGeoRP [Daten bearbeitet]",
        },
    ],
    [
        "thueringenviewer.thueringen.de",
        {
            title: "BORIS-TH",
            license: "dl-de/by-2-0 (www.govdata.de/dl-de/by-2-0) © Gutachterausschüsse für Grundstückswerte TH",
        },
    ],
    [
        "fbinter.stadt-berlin.de",
        {
            title: "BORIS GDI-BE",
            license:
                "dl-de/zero-2-0 (www.govdata.de/dl-de/zero-2-0) © Gutachterausschuss für Grundstückswerte in Berlin",
        },
    ],
]);

/**
 * Dienst zu einem BRW-`weblink`.
 * @param weblink `weblink` eines BRW aus `locationInformation/standardGroundValue`
 * @return Dienst, oder `null`, wenn der Link fehlt, keine gültige URL ist oder für seinen Host nichts hinterlegt ist
 */
export function groundValueServiceForWeblink(weblink: string | null | undefined): GroundValueService | null {
    if (!weblink || !URL.canParse(weblink)) return null;
    return GROUND_VALUE_SERVICES.get(new URL(weblink).host) ?? null;
}

/**
 * Prüft, ob für jeden BRW der Liste ein Dienst hinterlegt ist – jede BRW-Karte zeigt die Quelle ihres Dienstes.
 * @param groundValues BRW aus `locationInformation/standardGroundValue`
 */
export function allGroundValueServicesKnown(groundValues: readonly {weblink?: string | null}[]): boolean {
    return groundValues.every((groundValue) => groundValueServiceForWeblink(groundValue.weblink) !== null);
}
